import { ArrowDownRight, ArrowRight, ArrowUpRight, Receipt } from 'lucide-react';
import { useMemo } from 'react';
import { groupOf, useCategoryGroups, type DashboardData } from '../../app/data.ts';
import { useT } from '../../app/i18n.tsx';
import { hrefFor } from '../../app/router.tsx';
import { groupLabel, wholeGroup } from '../../ui/CategoryOptions.tsx';
import { Card, CategoryIcon, EmptyState, Money } from '../../ui/primitives.tsx';
import { categoryLabel } from '../../ui/TransactionRow.tsx';

// Where the period's money went, by main category: one bar each on one scale, its share of all the
// spending and how it moved against the previous period. More than six fold into "the rest".

type Row = { key: string; label: string; icon: string | null; color: string | null; now: bigint; before: bigint; href: string };

const SHOWN = 6;

export function SpendingCard({ data, className }: { data: DashboardData; className?: string }) {
  const t = useT();
  const groups = useCategoryGroups();
  const currency = data.household.baseCurrency;
  const rows = useMemo(() => {
    const byId = new Map(data.categories.map((c) => [c.id, c]));
    const mains = new Map<string, Row>();
    const add = (categoryId: string | null, amount: bigint, which: 'now' | 'before') => {
      if (amount <= 0n) return;
      const category = categoryId ? byId.get(categoryId) : undefined;
      const group = groupOf(category, groups);
      const key = group ? `g:${group.id}` : (categoryId ?? 'none');
      const row =
        mains.get(key) ??
        ({
          key,
          label: group ? groupLabel(group) : categoryLabel(t, category),
          icon: group?.icon ?? category?.icon ?? null,
          color: group?.color ?? category?.color ?? null,
          now: 0n,
          before: 0n,
          href: hrefFor({ page: 'transactions', categoryId: group ? wholeGroup(group.id) : (categoryId ?? 'none') }),
        } satisfies Row);
      row[which] += amount;
      mains.set(key, row);
    };
    for (const c of data.current.byCategory) add(c.categoryId, c.netSpendMinor, 'now');
    for (const c of data.previous.byCategory) add(c.categoryId, c.netSpendMinor, 'before');
    return [...mains.values()].filter((r) => r.now > 0n).sort((a, b) => (a.now === b.now ? 0 : a.now > b.now ? -1 : 1));
  }, [data, groups, t]);
  const total = rows.reduce((sum, r) => sum + r.now, 0n);
  const top = rows.slice(0, SHOWN);
  const rest = rows.slice(SHOWN).reduce((sum, r) => sum + r.now, 0n);
  const max = top[0]?.now ?? 1n;
  const share = (value: bigint) => (total > 0n ? Number(value) / Number(total) : 0);
  const pct = (value: number) => t.percent(value, { digits: 0 });

  return (
    <Card
      className={`spending ${className ?? ''}`}
      title={t.t('overview.spending')}
      subtitle={total > 0n ? <Money minor={total} currency={currency} /> : undefined}
      action={
        <a className="card__link" href={hrefFor({ page: 'transactions', kind: 'expenses' })}>
          {t.t('dashboard.viewAll')} <ArrowRight aria-hidden="true" />
        </a>
      }
    >
      {top.length === 0 ? (
        <EmptyState icon={<Receipt size={20} />} title={t.t('dashboard.spendingChangesEmpty')} />
      ) : (
        <ul className="spending__list">
          {top.map((row) => {
            const change = row.before > 0n ? Number(((row.now - row.before) * 1000n) / row.before) / 1000 : null;
            return (
              <li key={row.key}>
                <a className="spending__row" href={row.href}>
                  <CategoryIcon icon={row.icon} color={row.color} />
                  <span className="spending__what">
                    <span className="spending__line">
                      <span className="spending__name">{row.label}</span>
                      <strong className="spending__amount">
                        <Money minor={row.now} currency={currency} />
                      </strong>
                    </span>
                    <span className="spending__bar" aria-hidden="true">
                      <span style={{ width: `${Math.max(2, (Number(row.now) / Number(max)) * 100)}%`, background: row.color ?? 'var(--fill-neutral)' }} />
                    </span>
                    <span className="spending__meta">
                      <span>{t.t('overview.shareOfSpending', { percent: pct(share(row.now)) })}</span>
                      {change !== null && Math.abs(change) >= 0.005 && (
                        <span className={`spending__change ${change > 0 ? 'spending__change--up' : 'spending__change--down'}`}>
                          {change > 0 ? <ArrowUpRight aria-hidden="true" /> : <ArrowDownRight aria-hidden="true" />}
                          {t.percent(change, { digits: 0, signed: true })}
                        </span>
                      )}
                      {row.before === 0n && <span className="spending__new">{t.t('dashboard.newCategory')}</span>}
                    </span>
                  </span>
                </a>
              </li>
            );
          })}
          {rest > 0n && (
            <li className="spending__rest">
              <span>{t.plural('overview.restCategories', rows.length - SHOWN)}</span>
              <span>
                <Money minor={rest} currency={currency} /> · {pct(share(rest))}
              </span>
            </li>
          )}
        </ul>
      )}
    </Card>
  );
}
